import { CameraView, FlashMode, useCameraPermissions } from 'expo-camera'
import React, { useEffect, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Alert,
  SafeAreaView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { toqueColor } from '../../theme/colors'
import { Button } from '../Button'
import { DOBRA } from '../../theme/fonts'
import { useTranslation } from 'react-i18next'
import { FlashIcon } from '../Svg'
import { useStorage } from '../../hooks/useStorage'
import { useFocusEffect } from '@react-navigation/native'

interface Props {
  capturingText: string
  indicationsText: string
  getPhoto: (uri: string) => void
  skipBtn?: {
    show: boolean
    action: () => void
  }
  pauseBtn?: {
    show: boolean
    action: () => void
  }
}

export const TakePhoto = ({
  getPhoto,
  capturingText,
  indicationsText,
  skipBtn,
  pauseBtn,
}: Props) => {
  const [capturing, setCapturing] = useState(false)
  const { t } = useTranslation()
  const { bottom } = useSafeAreaInsets()
  const [permission, requestPermission] = useCameraPermissions()
  const [flashMode, setFlasMode] = useState<FlashMode>('off')
  const [isCameraActive, setIsCameraActive] = useState(false)

  const { isFirstRender } = useStorage({
    initialData: flashMode,
    key: 'flashMode',
    setDataFromStorage(data) {
      setFlasMode(data)
    },
  })

  const cameraRef = useRef<CameraView | null>(null)
  const handleTakePhoto = async () => {
    if (!cameraRef.current) return

    setCapturing(true)

    try {
      const photo = await cameraRef.current.takePictureAsync()
      cameraRef.current.pausePreview()
      cameraRef.current = null
      if (photo?.uri) {
        getPhoto(photo.uri)
      } else {
        Alert.alert('Error', 'No se pudo capturar la foto. Por favor, intente nuevamente.')
      }
    } catch (error) {
      console.error('Error al tomar la foto: ', error)
      Alert.alert('Error', 'No se pudo capturar la foto. Por favor, intente nuevamente.')
    } finally {
      setCapturing(false)
    }
  }

  useEffect(() => {
    if (!permission) {
      requestPermission()
    }
  }, [permission])

  useEffect(() => {
    isFirstRender.current = false
  }, [])

  useFocusEffect(
    React.useCallback(() => {
      setTimeout(() => {
        setIsCameraActive(true)
      }, 300)
      return () => {
        setIsCameraActive(false)
        if (cameraRef.current) {
          cameraRef.current.pausePreview()
          cameraRef.current = null
        }
      }
    }, []),
  )

  return (
    <SafeAreaView style={{ flex: 1 }}>
      {isCameraActive && (
        <CameraView
          style={[styles.camera, capturing && { opacity: 0.6 }]}
          ref={cameraRef}
          facing='back'
          flash={flashMode}
        >
          <View style={{ justifyContent: 'center', alignItems: 'flex-end' }}>
            <TouchableOpacity
              onPress={() => setFlasMode((prev) => (prev === 'off' ? 'on' : 'off'))}
            >
              <FlashIcon height={60} width={60} type={flashMode} />
            </TouchableOpacity>
          </View>
          <View style={[styles.textContainer, skipBtn?.show && { flex: 4 }]}>
            <Text style={styles.capturingText}>{t(capturingText)}</Text>
            <Text style={styles.secondText}>{t(indicationsText)}</Text>
          </View>

          <View
            style={[
              styles.buttonContainer,
              !skipBtn?.show && { flexDirection: 'column', justifyContent: 'flex-end', gap: 0 },
              (skipBtn?.show || pauseBtn?.show) && { flex: 1.1 },
              { paddingBottom: Math.max(bottom, 16) },
            ]}
          >
            {skipBtn?.show && (
              <Button
                onPress={() => {
                  if (cameraRef.current) {
                    cameraRef.current.pausePreview()
                    cameraRef.current = null
                    skipBtn.action()
                  }
                }}
                label={t('Omitir')}
                variant='white'
                customStyle={{ width: 100 }}
              />
            )}
            <TouchableOpacity
              style={[styles.captureButton]}
              onPress={capturing ? undefined : handleTakePhoto}
              activeOpacity={0.7}
            >
              {capturing && <ActivityIndicator size={30} />}
            </TouchableOpacity>
            {pauseBtn?.show && (
              <Button
                onPress={() => {
                  if (cameraRef.current) {
                    cameraRef.current.pausePreview()
                    cameraRef.current = null
                  }
                  pauseBtn.action()
                }}
                label={t('Pausar')}
                variant='white'
                customStyle={{ width: 100 }}
              />
            )}
          </View>
        </CameraView>
      )}
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  camera: {
    flex: 1,
    padding: 24,
    gap: 12,
  },

  textContainer: {
    flex: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderStyle: 'dashed',
    borderColor: 'white',
    borderWidth: 1,
    borderRadius: 10,
    zIndex: 500,
  },
  capturingText: {
    fontFamily: DOBRA,
    fontSize: 18,
    textAlign: 'center',
    opacity: 1,
    color: 'white',
    fontWeight: '500',
    width: 137,
    marginBottom: 23,
    lineHeight: 22,
  },
  secondText: {
    fontFamily: DOBRA,
    fontSize: 16,
    textAlign: 'center',
    opacity: 1,
    color: 'white',
    fontWeight: '400',
    width: 200,
    lineHeight: 22,
  },

  buttonContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    gap: 24,
  },

  captureButton: {
    backgroundColor: toqueColor,
    width: 50,
    height: 50,
    borderRadius: 50,
    justifyContent: 'center',
    zIndex: 1000,
  },
})
